/**
 * Shopify Combined Listings bridge for the legacy Natural Catch product template.
 *
 * Shopify exposes sibling child products through product_option_value.product_url.
 * The current theme predates that API and binds its variant controls at page load,
 * so switching products requires replacing the product-page content and then
 * reinitializing the existing gallery, variant, payment, and review integrations.
 */
(function () {
  if (window.ncCombinedListingBridgeLoaded) return;
  window.ncCombinedListingBridgeLoaded = true;

  let activeRequest = null;
  let galleryRefreshTimer = null;

  // Slick measures slide heights when the swapped-in gallery is initialized,
  // before lazysizes has loaded the images, and adaptiveHeight then pins
  // .slick-list to the placeholder's 1px. Re-measure as lazy images arrive.
  document.addEventListener('lazyloaded', function (event) {
    const target = event.target;
    if (!(target instanceof Element) || !window.jQuery) return;
    const gallery = target.closest('.p-thumb.slick-initialized');
    if (!gallery) return;

    clearTimeout(galleryRefreshTimer);
    galleryRefreshTimer = setTimeout(function () {
      if (gallery.classList.contains('slick-initialized')) {
        window.jQuery(gallery).slick('setPosition');
      }
    }, 60);
  });

  function getOptionSwatch(target) {
    if (!(target instanceof Element)) return null;
    // Also internal preview swatches placed in the theme's own picker (Teriyaki
    // on the multi-flavor page).
    return target.closest('[data-nc-combined-options] .nt-swatch, .nt-swatch[data-nc-preview="true"]');
  }

  function syncOptionAriaCurrent(swatch) {
    const group = swatch.closest('.swatch');
    if (!group) return;

    group.querySelectorAll('.nt-swatch a').forEach(function (link) {
      if (link === swatch.querySelector('a')) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  }

  function productPath(url) {
    try {
      return new URL(url, window.location.origin).pathname.replace(/\/+$/, '');
    } catch (error) {
      return '';
    }
  }

  function selectedOptionValueIds(clickedSwatch) {
    const picker = clickedSwatch.closest('[data-nc-combined-options]');
    if (!picker) return [];

    return Array.from(picker.querySelectorAll('.swatch'))
      .sort(function (a, b) {
        return Number(a.dataset.optionIndex || 0) - Number(b.dataset.optionIndex || 0);
      })
      .map(function (group) {
        const clickedGroup = clickedSwatch.closest('.swatch');
        const selected = group === clickedGroup
          ? clickedSwatch
          : group.querySelector('.nt-swatch.is-selected');
        return selected && selected.dataset.optionValueId;
      })
      .filter(Boolean);
  }

  function requestUrlFor(swatch) {
    const url = new URL(swatch.dataset.productUrl, window.location.origin);
    // Shopify's product_url normally contains only the product path. Preserve
    // the active theme preview context when switching siblings so a draft
    // listing never falls through to the published theme during QA.
    ['preview_theme_id', '_ab', '_fd', '_sc', 'pb'].forEach(function (key) {
      const value = new URL(window.location.href).searchParams.get(key);
      if (value) url.searchParams.set(key, value);
    });
    url.searchParams.delete('variant');
    // A draft preview fetched with preview_theme_id makes Shopify redirect and
    // answer with whichever draft this session previewed last (another draft),
    // not the requested one. The page already runs on the preview theme, whose
    // session cookie carries the theme, so drop the parameter for these fetches.
    if (url.pathname === '/products_preview') url.searchParams.delete('preview_theme_id');
    // Internal preview swatches (Teriyaki while it is a draft) cross between
    // products that share no option values, so ids from this page would not
    // resolve on the other one. Let each product open on its default variant.
    if (swatch.dataset.ncPreview === 'true') return url;
    const optionValueIds = selectedOptionValueIds(swatch);
    if (optionValueIds.length) {
      url.searchParams.set('option_values', optionValueIds.join(','));
    }
    return url;
  }

  // The menus are rendered once, server side, for the product the page first
  // loaded. A flavor swap replaces the product content without a reload, so
  // move the "current page" marker to the product now shown. Only leaf items
  // that link to a product page are touched.
  function syncMenuActiveItem(pathname) {
    document.querySelectorAll('li.menu-item:not(.has-sub) > a[href*="/products/"]').forEach(function (link) {
      const item = link.parentElement;
      // Top-level desktop items use "active" for an open dropdown, which paints
      // a grey block behind the label. Only sub-menu links carry this marker.
      if (item.parentElement && item.parentElement.id === 'nt_main_menu') return;
      const isCurrent = productPath(link.getAttribute('href')) === pathname;
      item.classList.toggle('active', isCurrent);
      item.classList.toggle('current_page_item', isCurrent);
    });
  }

  function productData() {
    const element = document.getElementById('ProductJson-template');
    if (!element) return null;

    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.error('Combined listing product data could not be read.', error);
      return null;
    }
  }

  function selectChildVariant(swatch) {
    const variantId = swatch.dataset.variantId;
    if (!variantId) return false;

    const picker = swatch.closest('[data-nc-combined-options]');
    const form = swatch.closest('form');
    const variantSelector = form && form.querySelector('[name="id"]');
    if (!picker || !variantSelector) return false;

    const group = swatch.closest('.swatch');
    if (group) {
      group.querySelectorAll('.nt-swatch').forEach(function (option) {
        option.classList.toggle('is-selected', option === swatch);
      });
    }
    syncOptionAriaCurrent(swatch);

    variantSelector.value = variantId;

    const data = productData();
    const variant = data && data.variants.find(function (candidate) {
      return String(candidate.id) === String(variantId);
    });

    if (variant && window.jQuery && window.elessiShopifyPre &&
        typeof window.elessiShopifyPre.selectCallback === 'function') {
      window.elessiShopifyPre.selectCallback(
        variant,
        '.nt_cart_form',
        '.product-select_pr',
        picker.querySelectorAll('.swatch').length,
        '#callBackVariant',
        '.nt_select_pr_',
        '',
        data
      );
    }

    variantSelector.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function copyProductJson(nextDocument, id) {
    const current = document.getElementById(id);
    const next = nextDocument.getElementById(id);

    if (current && next) {
      current.textContent = next.textContent;
    } else if (!current && next) {
      document.head.appendChild(document.importNode(next, true));
    } else if (current && !next) {
      current.remove();
    }
  }

  function replaceStickyAddToCart(nextDocument) {
    const current = document.querySelector('.sticky-nt-atc');
    const next = nextDocument.querySelector('.sticky-nt-atc');

    if (current && next) {
      current.replaceWith(document.importNode(next, true));
    } else if (current && !next) {
      current.remove();
    }
  }

  function normalizeLinkedOptions(root) {
    (root || document).querySelectorAll(
      '[data-nc-combined-options] .nt-swatch[data-product-url][data-option-available="true"]'
    ).forEach(function (swatch) {
      // The legacy variant script only sees variants on the current child product.
      // A sibling can therefore be falsely marked unavailable even though Shopify's
      // product_option_value says it is available.
      swatch.classList.remove('nt_unavailable', 'nt_soldout');
      const link = swatch.querySelector('a');
      if (link) link.removeAttribute('aria-disabled');
    });
  }

  function reinitializeThemeProduct() {
    if (!window.jQuery || !window.elessiShopifyPre) return;

    const $ = window.jQuery;
    const productJsonElement = document.getElementById('ProductJson-NT');
    const templateJsonElement = document.getElementById('ProductJson-template');

    if (typeof window.elessiShopifyPre.initCarousel === 'function') {
      window.elessiShopifyPre.initCarousel();
    }
    if (typeof window.elessiShopifyPre.initMasonry === 'function') {
      window.elessiShopifyPre.initMasonry();
    }

    if (productJsonElement && templateJsonElement) {
      try {
        const variantData = JSON.parse(productJsonElement.textContent);
        const productData = JSON.parse(templateJsonElement.textContent);

        $('.nt_select_pr_1 .is-selected-none').addClass('is-selected').removeClass('is-selected-none');
        $('.nt_select_pr_2 .is-selected-none').addClass('is-selected').removeClass('is-selected-none');

        window.elessiShopifyPre.Ntproduct_switch(
          '.variations_form',
          variantData,
          productData,
          '.nt_cart_form',
          '.product-select_pr',
          '.nt_select_pr_',
          '#callBackVariant',
          ''
        );

        if (window.NTsettingspr) {
          window.NTsettingspr.ProductID = productData.id;
          window.NTsettingspr.PrHandle = productData.handle;
        }

        // On a normal page load the theme applies the selected variant's state
        // (sold-out: hide Add to cart and the wallet buttons, show OUT OF STOCK).
        // Ntproduct_switch does not, so apply it for the variant in the form.
        const variantInput = document.querySelector('#nathan-content [name="id"]');
        const selectedVariant = variantInput && productData.variants.find(function (candidate) {
          return String(candidate.id) === String(variantInput.value);
        });
        // The theme marks the selected size swatch during its own page init, not in
        // Ntproduct_switch, so a swapped-in page shows "Size:" with nothing picked.
        if (selectedVariant) {
          const handles = (selectedVariant.options || []).map(function (value) {
            return String(value).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
          });
          document.querySelectorAll('#nathan-content [class*="nt_select_pr_"]').forEach(function (group) {
            if (group.querySelector('.nt-swatch.is-selected')) return;
            const match = Array.prototype.find.call(group.querySelectorAll('.nt-swatch'), function (option) {
              return handles.indexOf(option.dataset.value) !== -1;
            });
            if (match) {
              match.classList.add('is-selected');
              syncOptionAriaCurrent(match);
            }
          });
        }
        if (selectedVariant && typeof window.elessiShopifyPre.selectCallback === 'function') {
          window.elessiShopifyPre.selectCallback(
            selectedVariant,
            '.nt_cart_form',
            '.product-select_pr',
            (productData.options || []).length,
            '#callBackVariant',
            '.nt_select_pr_',
            '',
            productData
          );
        }
        if (window.nathan_settings) {
          window.nathan_settings.price_varies = productData.price_varies;
        }
      } catch (error) {
        console.error('Combined listing variant controls could not be initialized.', error);
      }
    }

    normalizeLinkedOptions(document);

    if (window.Shopify && window.Shopify.PaymentButton &&
        typeof window.Shopify.PaymentButton.init === 'function') {
      window.Shopify.PaymentButton.init();
    }

    // One-shot widgets the theme only initializes on page load; the swapped-in
    // DOM arrives with their placeholders empty. Same guards as jas_theme.js.
    if (window.elessiShopify && window.nathan_settings) {
      if (window.nathan_settings.flash_sold &&
          typeof window.elessiShopify.flashSoldBar === 'function') {
        window.elessiShopify.flashSoldBar('');
      }
      if (window.nathan_settings.stock_countdown &&
          typeof window.elessiShopify.progressbar === 'function') {
        window.elessiShopify.progressbar('.nt_progress_bar_pr');
      }
      if ((window.nathan_settings.enable_delivery_option ||
           window.nathan_settings.enable_delivery_order) &&
          typeof window.elessiShopify.delivery_order === 'function') {
        window.elessiShopify.delivery_order('#nt_product_delivery');
      }
    }

    if (window.yotpo && typeof window.yotpo.initWidgets === 'function') {
      // A reviews-widget failure must not abort the switch and trigger the
      // full-navigation fallback.
      try {
        window.yotpo.initWidgets();
      } catch (error) {
        console.error('Combined listing review widgets could not be reinitialized.', error);
      }
    }
  }

  // ── Page cache and prefetch ──
  // A swap waits almost entirely on Shopify rendering the next product page
  // (~0.4 s; the DOM swap itself takes a few ms). Start that request as soon as a
  // shopper points at or touches a swatch, reuse it on click, and keep it for a
  // few minutes so returning to a flavor is instant. Prices and stock can change,
  // hence the short lifetime.
  const PAGE_CACHE_MS = 5 * 60 * 1000;
  const pageCache = new Map();

  function fetchPage(url) {
    const key = url.toString();
    const hit = pageCache.get(key);
    if (hit && Date.now() - hit.time < PAGE_CACHE_MS) return hit.promise;

    function load(url) {
      return fetch(url, {
        headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin'
      }).then(function (response) {
        if (!response.ok) throw new Error('Product request failed with status ' + response.status);
        return response.text();
      });
    }
    const promise = load(key);
    promise.catch(function () { pageCache.delete(key); });
    promise.then(preloadMainImage).catch(function () {});
    pageCache.set(key, { promise: promise, time: Date.now() });
    return promise;
  }

  function preloadMainImage(html) {
    const match = /class="[^"]*p-thumb[\s\S]*?<img[^>]+?(?:data-src|src)="([^"]+)"/.exec(html);
    if (!match) return;
    const src = match[1].replace(/&amp;/g, '&').replace('{width}', '720');
    const image = new Image();
    image.decoding = 'async';
    image.src = src.indexOf('//') === 0 ? window.location.protocol + src : src;
  }

  function prefetchSwatch(swatch) {
    if (!swatch || !swatch.dataset.productUrl) return;
    if (swatch.classList.contains('is-selected')) return;
    if (productPath(swatch.dataset.productUrl) === productPath(window.location.href)) return;
    fetchPage(requestUrlFor(swatch)).catch(function () {});
  }

  function onSwatchIntent(event) {
    prefetchSwatch(getOptionSwatch(event.target));
  }
  document.addEventListener('pointerover', onSwatchIntent, { passive: true });
  document.addEventListener('touchstart', onSwatchIntent, { passive: true });
  document.addEventListener('focusin', onSwatchIntent);

  // ── Skeleton loader ──
  // Avoid "popcorning": while a slow swap is in flight, cover the product area
  // with placeholder shapes laid over the current photo and summary columns; when
  // the new product lands, hold it invisible until its main photo is ready (or a
  // short timeout), then fade it in. Cached swaps skip the skeleton entirely.
  const SKELETON_DELAY_MS = 300;
  const SETTLE_TIMEOUT_MS = 900;

  (function injectSkeletonStyles() {
    if (document.getElementById('nc-skeleton-styles')) return;
    const style = document.createElement('style');
    style.id = 'nc-skeleton-styles';
    style.textContent =
      '.nc-skel-layer{position:absolute;z-index:20;pointer-events:none;animation:nc-skel-in 160ms ease}' +
      '@keyframes nc-skel-in{from{opacity:0}to{opacity:1}}' +
      '.nc-skel-panel{position:absolute;background:#fff}' +
      '.nc-skel{display:block;border-radius:6px;background:linear-gradient(90deg,#eef0f3 25%,#f7f8fa 37%,#eef0f3 63%);background-size:400% 100%;animation:nc-skel-shimmer 1.3s ease infinite}' +
      '.nc-skel+.nc-skel{margin-top:12px}' +
      '.nc-skel--media{width:100%;height:100%;border-radius:8px}' +
      '.nc-skel-chips{display:flex;flex-wrap:wrap;gap:8px;margin-top:16px}' +
      '.nc-skel-chips .nc-skel{margin:0;height:34px;width:96px}' +
      '@keyframes nc-skel-shimmer{0%{background-position:100% 50%}100%{background-position:0 50%}}' +
      '.nc-swap-ghost{position:absolute;z-index:19;pointer-events:none;background:#fff;overflow:hidden;opacity:1;transition:opacity 220ms ease}' +
      '.nc-swap-ghost.is-fading{opacity:0}' +
      '@media (prefers-reduced-motion: reduce){.nc-skel{animation:none}.nc-swap-ghost{transition:none}}';
    document.head.appendChild(style);
  })();

  function skeletonSummaryHtml() {
    return '<span class="nc-skel" style="height:34px;width:70%"></span>' +
      '<span class="nc-skel" style="height:14px;width:35%"></span>' +
      '<span class="nc-skel" style="height:14px;width:45%"></span>' +
      '<span class="nc-skel" style="height:30px;width:32%;margin-top:20px"></span>' +
      '<span class="nc-skel" style="height:12px;width:92%"></span>' +
      '<span class="nc-skel" style="height:12px;width:84%"></span>' +
      '<div class="nc-skel-chips"><span class="nc-skel"></span><span class="nc-skel"></span><span class="nc-skel"></span></div>' +
      '<span class="nc-skel" style="height:48px;width:100%;margin-top:24px"></span>';
  }

  function showSkeleton(content) {
    const area = content.querySelector('.product-image-summary') || content;
    const rect = area.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const layer = document.createElement('div');
    layer.className = 'nc-skel-layer';
    layer.setAttribute('aria-hidden', 'true');
    layer.style.left = rect.left + window.scrollX + 'px';
    layer.style.top = rect.top + window.scrollY + 'px';
    layer.style.width = rect.width + 'px';
    layer.style.height = Math.min(rect.height, window.innerHeight * 1.5) + 'px';

    function panel(element, html) {
      if (!element) return;
      const r = element.getBoundingClientRect();
      if (!r.width || !r.height) return;
      const node = document.createElement('div');
      node.className = 'nc-skel-panel';
      node.style.left = r.left - rect.left + 'px';
      node.style.top = r.top - rect.top + 'px';
      node.style.width = r.width + 'px';
      node.style.height = Math.min(r.height, window.innerHeight * 1.5) + 'px';
      node.innerHTML = html;
      layer.appendChild(node);
    }
    const media = area.querySelector('.single-product-thumbnail, .p-thumb, .product-img-wrap');
    const mediaColumn = media && (media.closest('[class*="col-md-6"]') || media);
    panel(mediaColumn, '<span class="nc-skel nc-skel--media"></span>');
    panel(area.querySelector('.product-infors, .summary'), skeletonSummaryHtml());
    if (!layer.children.length) return null;
    document.body.appendChild(layer);
    return layer;
  }

  function settleNewContent(content) {
    const images = Array.from(content.querySelectorAll('.p-thumb img, .single-product-thumbnail img'));
    const main = images.find(function (image) { return image.getBoundingClientRect().width > 150; }) || images[0];
    // The theme lazy-loads gallery photos: the <img> first holds a tiny placeholder
    // (which is already "complete"), then lazysizes swaps in the real srcset and
    // adds .lazyloaded. Wait for that, not for the placeholder.
    const isReal = function () {
      return main && main.complete && main.naturalWidth > 0 &&
        (!main.classList.contains('lazyload') || main.classList.contains('lazyloaded'));
    };
    const ready = !main || isReal() ? Promise.resolve() : new Promise(function (resolve) {
      function check() { if (isReal()) resolve(); }
      main.addEventListener('lazyloaded', function () {
        if (main.decode) main.decode().then(resolve, resolve); else resolve();
      }, { once: true });
      main.addEventListener('load', check);
      const poll = setInterval(function () { if (isReal()) { clearInterval(poll); resolve(); } }, 50);
      setTimeout(function () { clearInterval(poll); }, SETTLE_TIMEOUT_MS);
    });
    const stable = waitForStableLayout(content);
    return Promise.race([
      Promise.all([ready, stable]),
      new Promise(function (resolve) { setTimeout(resolve, SETTLE_TIMEOUT_MS); })
    ]);
  }

  // Resolve once the product block has not changed size for STABLE_QUIET_MS
  // (after at least STABLE_MIN_MS, so late theme widgets get their turn).
  const STABLE_QUIET_MS = 120;
  const STABLE_MIN_MS = 150;
  function waitForStableLayout(content) {
    const target = content.querySelector('.product-image-summary') || content;
    if (!('ResizeObserver' in window)) {
      return new Promise(function (resolve) { setTimeout(resolve, STABLE_MIN_MS + STABLE_QUIET_MS); });
    }
    return new Promise(function (resolve) {
      const started = performance.now();
      let quietTimer = null;
      const observer = new ResizeObserver(arm);
      function arm() {
        clearTimeout(quietTimer);
        const wait = Math.max(STABLE_QUIET_MS, STABLE_MIN_MS - (performance.now() - started));
        quietTimer = setTimeout(function () { observer.disconnect(); resolve(); }, wait);
      }
      observer.observe(target);
      target.querySelectorAll('.summary, .product-infors, .p-thumb, .single-product-thumbnail').forEach(function (element) {
        observer.observe(element);
      });
      arm();
    });
  }


  // A non-interactive visual copy of the current photo + summary block, pinned
  // over the same spot, so the swap never shows a blank or half-built product.
  function freezeCurrentProduct(content) {
    const block = content && content.querySelector('.product-image-summary');
    if (!block) return null;
    const rect = block.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const ghost = block.cloneNode(true);
    ghost.removeAttribute('id');
    ghost.querySelectorAll('[id]').forEach(function (element) { element.removeAttribute('id'); });
    ghost.querySelectorAll('form, input, select, button, a').forEach(function (element) {
      element.setAttribute('tabindex', '-1');
    });
    ghost.setAttribute('aria-hidden', 'true');
    ghost.classList.add('nc-swap-ghost');
    ghost.style.top = (rect.top + window.scrollY) + 'px';
    ghost.style.left = (rect.left + window.scrollX) + 'px';
    ghost.style.width = rect.width + 'px';
    ghost.style.height = rect.height + 'px';
    document.body.appendChild(ghost);
    return ghost;
  }

  async function switchProduct(swatch) {
    const currentContent = document.getElementById('nathan-content');
    if (!currentContent) return;

    if (activeRequest) activeRequest.abort();
    const requestController = new AbortController();
    activeRequest = requestController;

    const requestUrl = requestUrlFor(swatch);
    const previousScrollY = window.scrollY;
    const optionValueId = swatch.dataset.optionValueId;

    currentContent.classList.add('nc-combined-listing-loading');
    currentContent.setAttribute('aria-busy', 'true');

    let skeletonLayer = null;
    const skeletonTimer = setTimeout(function () {
      // By now this may be the new, still-invisible product; its layout is real.
      skeletonLayer = showSkeleton(document.getElementById('nathan-content') || currentContent);
    }, SKELETON_DELAY_MS);
    function clearSkeleton() {
      clearTimeout(skeletonTimer);
      if (skeletonLayer) skeletonLayer.remove();
      skeletonLayer = null;
    }

    try {
      const responseText = await fetchPage(requestUrl);
      // A newer pick superseded this one while it loaded.
      if (requestController.signal.aborted) { clearSkeleton(); return; }
      const nextDocument = new DOMParser().parseFromString(responseText, 'text/html');
      const nextContent = nextDocument.getElementById('nathan-content');

      if (!nextContent) throw new Error('The selected product response did not include product content.');

      copyProductJson(nextDocument, 'ProductJson-NT');
      copyProductJson(nextDocument, 'ProductJson-template');
      replaceStickyAddToCart(nextDocument);

      const importedContent = document.importNode(nextContent, true);
      // The theme fills the gallery carousel, reviews and "sold in last hours" bar
      // a moment after the swap, and each pushes the page. Instead of blanking the
      // page while that settles, keep a frozen copy of the current photo + summary
      // on top, then cross-fade it away once the new product is steady.
      // A slow swap already shows the skeleton: keep it as the cover and fade it
      // out, rather than flashing the old product back in between.
      clearTimeout(skeletonTimer);
      let ghost = skeletonLayer;
      skeletonLayer = null;
      if (ghost) ghost.classList.add('nc-swap-ghost');
      else ghost = freezeCurrentProduct(currentContent);
      currentContent.replaceWith(importedContent);
      document.title = nextDocument.title;
      window.history.replaceState(
        { path: requestUrl.pathname + requestUrl.search },
        '',
        requestUrl.pathname + requestUrl.search
      );

      reinitializeThemeProduct();
      syncMenuActiveItem(productPath(requestUrl.pathname));

      if (ghost) {
        settleNewContent(document.getElementById('nathan-content')).then(function () {
          ghost.classList.add('is-fading');
          setTimeout(function () { ghost.remove(); }, 260);
        });
      }
      window.scrollTo(0, previousScrollY);

      document.dispatchEvent(new CustomEvent('nc:combined-product:loaded', {
        detail: {
          productUrl: requestUrl.pathname,
          optionValueId: optionValueId
        }
      }));

      if (optionValueId) {
        const selected = document.querySelector(
          '[data-nc-combined-options] [data-option-value-id="' +
          CSS.escape(optionValueId) +
          '"] a'
        );
        if (selected) selected.focus({ preventScroll: true });
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      // A newer pick superseded this one: its failure must not navigate away from it.
      if (requestController.signal.aborted || activeRequest !== requestController) return;

      clearSkeleton();
      console.error('Combined listing product switch failed.', error);
      currentContent.classList.remove('nc-combined-listing-loading');
      currentContent.removeAttribute('aria-busy');

      // Preserve a functional storefront if an app, proxy, or browser extension
      // blocks the asynchronous request.
      window.location.assign(requestUrl.toString());
    } finally {
      if (activeRequest === requestController) {
        activeRequest = null;
      }
    }
  }

  function loadSelectedChildFromParent() {
    const selectedLinkedSwatch = document.querySelector(
      '[data-nc-combined-options] .nt-swatch.is-selected[data-product-url]'
    );
    if (!selectedLinkedSwatch) return;

    const targetPath = productPath(selectedLinkedSwatch.dataset.productUrl);
    const currentPath = productPath(window.location.href);
    if (targetPath && targetPath !== currentPath) {
      switchProduct(selectedLinkedSwatch);
    }
  }

  document.addEventListener('click', function (event) {
    const swatch = getOptionSwatch(event.target);
    if (!swatch) return;

    if (!swatch.dataset.productUrl) {
      if (!swatch.classList.contains('nt_unavailable') &&
          !swatch.classList.contains('nt_soldout')) {
        if (selectChildVariant(swatch)) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
        } else {
          syncOptionAriaCurrent(swatch);
        }
      }
      return;
    }

    const targetPath = productPath(swatch.dataset.productUrl);
    const currentPath = productPath(window.location.href);
    if (!targetPath) return;


    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (targetPath === currentPath) return;

    switchProduct(swatch);
  }, true);

  normalizeLinkedOptions(document);
  loadSelectedChildFromParent();
})();
